/**
 * TalentRegionGate — iter-136
 *
 * Non-dismissable dashboard banner + inline flow that walks a talent
 * through the four US-only signup checks:
 *   1. Phone SMS verify (Twilio Verify + Lookup; degrades to manual)
 *   2. Work-authorization attestation (checkbox only — never asks
 *      citizenship/national origin/visa)
 *   3. US state + ZIP
 *   4. Server-side IP-geo already checked at signup — surfaced here
 *      as informational
 *
 * Only renders when region_status != 'verified_us'.
 */
import React, { useCallback, useEffect, useState } from "react";
import { ShieldCheck, ShieldAlert, Loader2, CheckCircle2, MapPin, Phone } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const US_STATES = [
    "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN",
    "IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV",
    "NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN",
    "TX","UT","VT","VA","WA","WV","WI","WY","DC",
];

export default function TalentRegionGate() {
    const [status, setStatus] = useState(null);
    const [phase, setPhase] = useState("summary"); // summary | phone | code | attest | done
    const [busy, setBusy] = useState(false);
    const [phone, setPhone] = useState("+1");
    const [code, setCode] = useState("");
    const [state, setState] = useState("");
    const [zip, setZip] = useState("");
    const [workAuth, setWorkAuth] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/talent/region/status");
            setStatus(data);
        } catch {
            /* not a talent, or not authed — silently render nothing */
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    if (!status) return null;
    if (status.region_status === "verified_us") return null;

    const stepsDone = status.steps || {};
    const flagged = status.region_status === "non_us_flagged";

    const startPhone = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/talent/region/phone/start", { phone });
            if (data.status === "not_configured") {
                toast.message("SMS is being reviewed by our team", {
                    description: "Our verification service is warming up. An admin will manually verify your number — you can continue with the rest of the steps.",
                });
            } else {
                toast.success("Check your phone for a 6-digit code.");
            }
            setPhase("code");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to send code.");
        } finally { setBusy(false); }
    };

    const checkCode = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/talent/region/phone/check", { code });
            if (data.status === "not_configured") {
                toast.message("Awaiting admin verification of your number.");
            } else if (data.status === "verified") {
                toast.success("Phone verified.");
            }
            setPhase("attest");
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Invalid code.");
        } finally { setBusy(false); }
    };

    const submitAttest = async () => {
        if (!workAuth) {
            toast.error("Please confirm work authorization to continue.");
            return;
        }
        setBusy(true);
        try {
            const { data } = await api.post("/talent/region/attest", {
                work_auth_attested: workAuth,
                us_address_state: state,
                us_address_zip: zip,
            });
            if (data.region_status === "verified_us") {
                toast.success("You're verified — welcome to Simonara!");
                setPhase("done");
            } else {
                toast.success("Details saved. Verification will finalise shortly.");
            }
            await load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed.");
        } finally { setBusy(false); }
    };

    // Banner-only summary (default)
    return (
        <div
            className={`rounded-2xl border p-5 ${flagged ? "bg-orange-50 border-orange-200 text-orange-900" : "bg-sky-50 border-sky-200 text-sky-900"}`}
            data-testid="talent-region-gate"
        >
            <div className="flex items-start gap-3">
                {flagged ? <ShieldAlert size={22} className="mt-0.5 shrink-0" /> : <ShieldCheck size={22} className="mt-0.5 shrink-0" />}
                <div className="flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide opacity-80">Account verification</div>
                    <h3 className="font-heading text-xl font-semibold mt-0.5">
                        {flagged
                            ? "This account was flagged as non-US"
                            : "Finish setting up your Simonara talent account"}
                    </h3>
                    <p className="text-sm mt-1">
                        {flagged
                            ? "Simonara currently serves professionals based in the US. Your account is on hold — reach out to hello@simonaratalent.us if this was a mistake."
                            : "Employers can't see your profile until you finish these three quick checks. Takes ~2 minutes."}
                    </p>

                    {!flagged && (
                        <ul className="mt-3 space-y-1 text-sm" data-testid="talent-region-steps">
                            <li className="flex items-center gap-2">
                                {stepsDone.phone_verified ? <CheckCircle2 size={14} className="text-emerald-600" /> : <Phone size={14} className="opacity-60" />}
                                US mobile phone verified
                            </li>
                            <li className="flex items-center gap-2">
                                {stepsDone.work_auth_attested ? <CheckCircle2 size={14} className="text-emerald-600" /> : <ShieldCheck size={14} className="opacity-60" />}
                                Work-authorization attested
                            </li>
                            <li className="flex items-center gap-2">
                                {stepsDone.address_captured ? <CheckCircle2 size={14} className="text-emerald-600" /> : <MapPin size={14} className="opacity-60" />}
                                State + ZIP on file
                            </li>
                        </ul>
                    )}

                    {!flagged && phase === "summary" && (
                        <button
                            onClick={() => setPhase(stepsDone.phone_verified ? "attest" : "phone")}
                            className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2"
                            data-testid="talent-region-start-btn"
                        >
                            {stepsDone.phone_verified ? "Continue verification" : "Start verification"}
                        </button>
                    )}

                    {phase === "phone" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-xs font-semibold">US mobile number (E.164)</label>
                            <input
                                value={phone} onChange={(e) => setPhone(e.target.value)}
                                placeholder="+14155551234"
                                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900"
                                data-testid="talent-region-phone"
                            />
                            <button onClick={startPhone} disabled={busy}
                                className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
                                data-testid="talent-region-phone-start">
                                {busy ? <Loader2 size={14} className="animate-spin inline" /> : "Send code"}
                            </button>
                        </div>
                    )}

                    {phase === "code" && (
                        <div className="mt-4 space-y-2">
                            <label className="block text-xs font-semibold">Enter the 6-digit code</label>
                            <input
                                value={code} onChange={(e) => setCode(e.target.value)}
                                inputMode="numeric" maxLength={10}
                                className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900 tracking-widest text-center"
                                data-testid="talent-region-code"
                            />
                            <button onClick={checkCode} disabled={busy}
                                className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
                                data-testid="talent-region-code-check">
                                {busy ? <Loader2 size={14} className="animate-spin inline" /> : "Verify"}
                            </button>
                        </div>
                    )}

                    {phase === "attest" && (
                        <div className="mt-4 space-y-3">
                            <label className="flex items-start gap-2 text-sm">
                                <input type="checkbox" checked={workAuth} onChange={(e) => setWorkAuth(e.target.checked)}
                                       data-testid="talent-region-workauth" className="mt-1" />
                                I confirm that I am authorized to work in the United States.
                            </label>
                            <div className="grid grid-cols-3 gap-2">
                                <select value={state} onChange={(e) => setState(e.target.value)}
                                        className="col-span-1 rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900"
                                        data-testid="talent-region-state">
                                    <option value="">State</option>
                                    {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                                <input value={zip} onChange={(e) => setZip(e.target.value)}
                                       placeholder="ZIP (12345 or 12345-6789)"
                                       className="col-span-2 rounded-lg border border-border bg-white px-3 py-2 text-sm text-slate-900"
                                       data-testid="talent-region-zip" />
                            </div>
                            <button onClick={submitAttest} disabled={busy || !workAuth || !state || !zip}
                                className="rounded-full bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 disabled:opacity-50"
                                data-testid="talent-region-attest-submit">
                                {busy ? <Loader2 size={14} className="animate-spin inline" /> : "Finish verification"}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
